import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Users, Link, Mail, X, Send, Copy, Clock,
  Trash2, XCircle, AlertCircle, Share2,
  Briefcase, Building2, Paperclip,
  LinkIcon, CheckCircle, Loader, Filter,
  Calendar, DollarSign, MessageSquare, Plus, Globe
} from 'lucide-react';
import { candidatesAPI, clientsAPI, jobsAPI } from "../../services/api";
import { toast } from "react-toastify";
import ShareCandidatesModal from './ShareCandidatesModal';

const SHARE_TYPES = {
  LINK: {
    key: 'LINK', label: 'Send Portal Link',
    sublabel: 'Client gets a URL to review candidates in their browser',
    icon: LinkIcon, color: 'indigo',
    apiMethod: (linkId) => candidatesAPI.sendLinkEmail(linkId),
    successMsg: 'Portal link email sent successfully!',
  },
  ATTACHMENT: {
    key: 'ATTACHMENT', label: 'Send Excel Attachment',
    sublabel: 'Client gets an Excel file with all candidate details',
    icon: Paperclip, color: 'emerald',
    apiMethod: (linkId) => candidatesAPI.sendAttachmentEmail(linkId),
    successMsg: 'Candidate list attachment sent successfully!',
  },
};

// ── ALL tabs now use blue as the active color (matching Interview_Scheduled style) ──
const STATUS_FILTERS = [
  { key: 'all',                  label: 'All'               },
  { key: 'Selected',             label: 'Selected / Hired'  },
  { key: 'Shortlisted',          label: 'Shortlisted'       },
  { key: 'Rejected',             label: 'Rejected'          },
  { key: 'On Hold',              label: 'On Hold'           },
  { key: 'Interview_Scheduled',  label: 'Interview Scheduled' },
  { key: 'Slotassigned',         label: 'Slot Assigned'     },
];

// All active tabs use blue; inactive tabs share a common inactive style
const ACTIVE_TAB   = 'bg-blue-600 text-white';
const INACTIVE_TAB = 'bg-white text-slate-500 border border-slate-200 hover:bg-blue-50 hover:text-blue-700';

const getStatusColor = (status) => {
  const map = {
    Applied:              'bg-blue-100 text-blue-800',
    Screening:            'bg-purple-100 text-purple-800',
    Interview_Scheduled:  'bg-blue-100 text-blue-800',
    'Interview Scheduled':'bg-blue-100 text-blue-800',
    Slotassigned:         'bg-blue-100 text-blue-800',
    Selected:             'bg-emerald-100 text-emerald-800',
    Rejected:             'bg-red-100 text-red-800',
    Shortlisted:          'bg-teal-100 text-teal-800',
    'On Hold':            'bg-amber-100 text-amber-800',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  try { return new Date(d).toLocaleString(); } catch { return 'N/A'; }
};

const COMMON_TIMEZONES_SHORT = [
  'Asia/Kolkata','Asia/Tokyo','Asia/Singapore','Asia/Dubai',
  'Europe/London','Europe/Paris','America/New_York','America/Los_Angeles','Australia/Sydney',
];

// ── Schedule Interview Modal (Add new slots) ────────────────────────────────
const ScheduleInterviewModal = ({ candidate, token, onClose, onScheduled }) => {
  const [slots, setSlots] = useState(['']);
  const [clientTimeZone, setClientTimeZone] = useState('Asia/Kolkata');
  const [busy, setBusy] = useState(false);

  const addSlot    = () => setSlots(prev => [...prev, '']);
  const removeSlot = (i) => setSlots(prev => prev.filter((_, idx) => idx !== i));
  const updateSlot = (i, value) => setSlots(prev => prev.map((s, idx) => idx === i ? value : s));

  const handleSubmit = async () => {
    const validSlots = slots.filter(s => s.trim());
    if (validSlots.length === 0) { toast.error('Please add at least one slot'); return; }
    if (!clientTimeZone) { toast.error('Please select a timezone'); return; }
    setBusy(true);
    try {
      await candidatesAPI.updateStatusByToken(token, candidate.id, {
        status: 'Interview Scheduled',
        clientTimeZone,
        timeslots: validSlots,
      });
      toast.success('Interview slots saved! Candidate can now select a slot.');
      onScheduled();
      onClose();
    } catch (err) {
      toast.error('Failed to schedule: ' + (err.response?.data?.message || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-0.5">Schedule Interview</p>
            <h2 className="text-base font-bold text-slate-900">{candidate.firstName} {candidate.lastName}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{candidate.primaryEmail}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
              <Globe size={13} className="text-slate-500" /> Client Timezone <span className="text-red-500">*</span>
            </label>
            <select value={clientTimeZone} onChange={e => setClientTimeZone(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
              {COMMON_TIMEZONES_SHORT.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1">Slots will be saved in this timezone.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar size={13} /> Interview Slots <span className="text-red-500">*</span>
              </label>
              <button onClick={addSlot} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                <Plus size={12} /> Add Slot
              </button>
            </div>
            <div className="space-y-2">
              {slots.map((slot, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl p-2.5 border border-slate-200">
                  <span className="text-xs font-bold text-slate-400 w-5 text-center">{i + 1}</span>
                  <input type="datetime-local" value={slot} onChange={e => updateSlot(i, e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                  {slots.length > 1 && (
                    <button onClick={() => removeSlot(i)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3 border-t border-slate-100 pt-4">
          <button onClick={onClose} disabled={busy} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
          <button onClick={handleSubmit} disabled={busy} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader size={14} className="animate-spin" /> : <Calendar size={14} />}
            {busy ? 'Saving...' : 'Save Slots'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Confirm Slot & Schedule Interview Modal ─────────────────────────────────
const ConfirmSlotModal = ({ candidate, selectedJob, onClose, onScheduled }) => {
  const [pickedSlot, setPickedSlot]         = useState(candidate.selectedSlot || null);
  const [candidateEmail, setCandidateEmail] = useState(candidate.primaryEmail || '');
  const [bccEmail, setBccEmail]             = useState('');
  const [userTimeZone, setUserTimeZone]     = useState(
    candidate.userTimeZone || candidate.clientTimeZone || 'Asia/Kolkata'
  );
  const [busy, setBusy]             = useState(false);
  const [showAllSlots, setShowAllSlots] = useState(!candidate.selectedSlot);

  const slots = Array.isArray(candidate.timeslots) ? candidate.timeslots : [];

  const formatSlot = (slot) => {
    try {
      const d = new Date(slot);
      if (isNaN(d)) return slot;
      return d.toLocaleString('en-IN', {
        weekday: 'short', day: 'numeric', month: 'short',
        year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return slot; }
  };

  const handleSchedule = async () => {
    if (!pickedSlot)     { toast.error('Please select a time slot'); return; }
    if (!candidateEmail) { toast.error('Candidate email is required'); return; }
    if (!userTimeZone)   { toast.error('Please select a timezone'); return; }
    setBusy(true);
    try {
      const convertRes = await candidatesAPI.getConvertedSlotsForCandidate(candidate.id, userTimeZone);
      const convertedSlots = convertRes.data?.convertedSlots || [];
      const rawSlots = Array.isArray(candidate.timeslots) ? candidate.timeslots : [];
      const slotIndex = rawSlots.indexOf(pickedSlot);
      const resolvedSlot = (slotIndex >= 0 && convertedSlots[slotIndex])
        ? convertedSlots[slotIndex]
        : pickedSlot;

      const schedulePayload = {
        candidateApplicationId: candidate.id,
        jobId:         selectedJob?.id || '',
        toEmail:       candidateEmail,
        candidateName: `${candidate.firstName} ${candidate.lastName}`,
        selectedSlot:  resolvedSlot,
        userTimeZone,
        bccEmail:      bccEmail.trim() || undefined,
        candidateEmail,
      };
      await candidatesAPI.scheduleInterview(schedulePayload);
      toast.success('Interview scheduled! Google Meet created & email sent.');
      onScheduled();
      onClose();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-0.5">Confirm & Schedule</p>
            <h2 className="text-base font-bold text-slate-900">{candidate.firstName} {candidate.lastName}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{candidate.primaryEmail}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 flex-shrink-0"><X size={18} /></button>
        </div>
        {pickedSlot && (
          <div className="flex items-center gap-3 px-6 py-3 bg-blue-600 flex-shrink-0">
            <CheckCircle size={16} className="text-white flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-blue-100 leading-none mb-0.5">
                {pickedSlot === candidate.selectedSlot ? "Proceeding with candidate's choice" : 'You changed to'}
              </p>
              <p className="text-sm font-bold text-white truncate">{formatSlot(pickedSlot)}</p>
            </div>
            <button onClick={() => { setPickedSlot(null); setShowAllSlots(true); }}
              className="p-1 rounded-lg hover:bg-blue-700 text-blue-200 hover:text-white flex-shrink-0" title="Change slot">
              <X size={13} />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-5">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
              <Globe size={13} /> Timezone <span className="text-red-500">*</span>
            </label>
            <select value={userTimeZone} onChange={e => setUserTimeZone(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
              {COMMON_TIMEZONES_SHORT.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar size={13} /> Interview Slots
              </label>
              {candidate.selectedSlot && slots.length > 0 && (
                <button onClick={() => setShowAllSlots(v => !v)} className="text-xs font-medium text-blue-600 hover:text-blue-700">
                  {showAllSlots ? 'Hide all slots' : `Change slot (${slots.length} available)`}
                </button>
              )}
            </div>
            {candidate.selectedSlot && (
              <div className={`rounded-xl border-2 p-4 mb-3 transition-colors ${pickedSlot === candidate.selectedSlot ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <CheckCircle size={15} className={`mt-0.5 flex-shrink-0 ${pickedSlot === candidate.selectedSlot ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Candidate selected</p>
                      <p className={`text-sm font-bold ${pickedSlot === candidate.selectedSlot ? 'text-emerald-800' : 'text-slate-700'}`}>
                        {formatSlot(candidate.selectedSlot)}
                      </p>
                    </div>
                  </div>
                  {pickedSlot !== candidate.selectedSlot ? (
                    <button onClick={() => { setPickedSlot(candidate.selectedSlot); setShowAllSlots(false); }}
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">Use this</button>
                  ) : (
                    <span className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-lg">✓ Selected / Hired</span>
                  )}
                </div>
              </div>
            )}
            {showAllSlots && (
              <div className="space-y-2">
                {slots.length === 0 ? (
                  <div className="flex flex-col items-center py-6 text-slate-400">
                    <Calendar size={28} className="mb-2 opacity-30" />
                    <p className="text-sm">No slots available</p>
                  </div>
                ) : (
                  <>
                    {candidate.selectedSlot && <p className="text-xs text-slate-400 mb-1">Or pick a different slot:</p>}
                    {slots.map((slot, i) => {
                      const isSelected      = pickedSlot === slot;
                      const isCandidatePick = candidate.selectedSlot === slot;
                      return (
                        <button key={i} onClick={() => { setPickedSlot(slot); if (candidate.selectedSlot) setShowAllSlots(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                            isSelected ? 'border-blue-500 bg-blue-50'
                            : isCandidatePick ? 'border-emerald-300 bg-emerald-50 hover:border-emerald-400'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                          }`}>
                          <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-semibold ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>{formatSlot(slot)}</div>
                            {isCandidatePick && (
                              <span className="inline-flex items-center gap-1 mt-0.5 text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                <CheckCircle size={10} /> Candidate's choice
                              </span>
                            )}
                          </div>
                          {isSelected && <CheckCircle size={16} className="text-blue-500 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
              Candidate Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="email" value={candidateEmail} onChange={e => setCandidateEmail(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
              BCC Email <span className="text-slate-400 font-normal normal-case text-xs">(optional)</span>
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="email" value={bccEmail} onChange={e => setBccEmail(e.target.value)}
                placeholder="recruiter@company.com"
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
            </div>
          </div>
          {pickedSlot && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs space-y-1.5">
              <p className="font-semibold text-blue-700">Scheduling summary</p>
              <div className="flex justify-between text-slate-600"><span>Slot</span><span className="font-medium text-slate-800 text-right ml-4">{formatSlot(pickedSlot)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Timezone</span><span className="font-medium text-slate-800">{userTimeZone}</span></div>
              <div className="flex justify-between text-slate-600"><span>Job</span><span className="font-medium text-slate-800">{selectedJob?.jobCode} · {selectedJob?.jobTitle}</span></div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0 bg-white rounded-b-2xl">
          <button onClick={onClose} disabled={busy} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSchedule} disabled={busy || !pickedSlot || !candidateEmail}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {busy ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? 'Scheduling…' : 'Confirm & Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Rejection Reason Modal ──────────────────────────────────────────────────
const RejectionReasonModal = ({ candidate, onClose }) => {
  const reason = candidate.reason || candidate.rejectionReason || null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-red-50 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-0.5">Rejection Reason</p>
            <h2 className="text-base font-bold text-slate-900">{candidate.firstName} {candidate.lastName}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          {reason ? (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-800 leading-relaxed">{reason}</div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-slate-400">
              <MessageSquare size={32} className="mb-2 opacity-30" />
              <p className="text-sm">No rejection reason recorded</p>
            </div>
          )}
        </div>
        <div className="px-6 pb-5">
          <button onClick={onClose} className="w-full px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50">Close</button>
        </div>
      </div>
    </div>
  );
};

// Replaced with shared ShareCandidatesModal component

// ── Main Component ──────────────────────────────────────────────────────────
const ClientCandidateSharing = () => {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [activeLinks, setActiveLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLinksPanel, setShowLinksPanel] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [expiryHours, setExpiryHours] = useState(48);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkFilter, setLinkFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scheduleCandidate, setScheduleCandidate] = useState(null);
  const [scheduleToken, setScheduleToken] = useState(null);
  const [confirmSlotCandidate, setConfirmSlotCandidate] = useState(null);
  const [rejectionCandidate, setRejectionCandidate] = useState(null);

  useEffect(() => { fetchClients(); }, []);

  // ── Handle incoming state from navigation (e.g. from JobDetailsPage) ──
  useEffect(() => {
    if (location.state?.jobId && clients.length > 0) {
      // 1. Try to find the client if clientName was passed
      const targetClient = clients.find(c => c.companyName === location.state.clientName);
      if (targetClient) {
        setSelectedClient(targetClient);
        // fetchJobsByClient will be triggered by another useEffect, 
        // but we need to wait for jobs to be loaded to select the job.
      }
    }
  }, [location.state, clients]);

  useEffect(() => {
    if (location.state?.jobId && jobs.length > 0) {
      const targetJob = jobs.find(j => j.id === location.state.jobId);
      if (targetJob) {
        setSelectedJob(targetJob);
      }
    }
  }, [location.state, jobs]);

  useEffect(() => {
    if (selectedClient) {
      fetchJobsByClient(selectedClient.id);
      setEmailInput(selectedClient.pocEmail || '');
      setSelectedJob(null); setCandidates([]); setSelectedCandidates([]);
      setStatusFilter('all');
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedJob) { fetchCandidatesByJobId(selectedJob.id); setStatusFilter('all'); }
  }, [selectedJob]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await clientsAPI.getAllClients();
      setClients(Array.isArray(res.data) ? res.data : res.data.content || []);
    } catch { toast.error('Failed to fetch clients'); }
    finally { setLoading(false); }
  };

  const fetchJobsByClient = async (clientId) => {
    setLoading(true);
    try {
      const res = await jobsAPI.getJobsByClientId(clientId);
      const data = Array.isArray(res.data) ? res.data : res.data.content || [];
      setJobs(data);
      if (data.length === 0) toast.info('No jobs found for this client');
    } catch { toast.error('Failed to fetch jobs'); setJobs([]); }
    finally { setLoading(false); }
  };

  const fetchCandidatesByJobId = async (jobId) => {
    setLoading(true);
    try {
      const res = await candidatesAPI.getApplicationsByClientId(selectedClient.id);
      let data = Array.isArray(res.data) ? res.data : res.data.content || [];
      data = data.filter(c => Array.isArray(c.jobIds) && c.jobIds.includes(jobId));
      setCandidates(data);
      toast.success(`${data.length} candidates loaded`);
    } catch { toast.error('Failed to fetch candidates'); setCandidates([]); }
    finally { setLoading(false); }
  };

  const fetchActiveLinks = async () => {
    setLoading(true);
    try {
      const res = await candidatesAPI.getActiveLinks();
      setActiveLinks(res.data?.links || res.data || []);
      setShowLinksPanel(true);
    } catch { toast.error('Failed to fetch active links'); }
    finally { setLoading(false); }
  };

  const handleRevokeLink = async (linkId) => {
    if (!window.confirm('Revoke this link?')) return;
    setLoading(true);
    try { await candidatesAPI.revokeLink(linkId); toast.success('Link revoked'); fetchActiveLinks(); }
    catch { toast.error('Failed to revoke link'); }
    finally { setLoading(false); }
  };

  const handleExtendLink = async (linkId) => {
    const h = prompt('Hours to extend by:', '24');
    if (!h) return;
    const hours = Number(h);
    if (isNaN(hours) || hours <= 0) { toast.error('Enter a valid number'); return; }
    setLoading(true);
    try { await candidatesAPI.extendLink(linkId, hours); toast.success(`Extended by ${hours} hours`); fetchActiveLinks(); }
    catch { toast.error('Failed to extend link'); }
    finally { setLoading(false); }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Mark all expired links?')) return;
    setLoading(true);
    try { await candidatesAPI.cleanupExpiredLinks(); toast.success('Expired links cleaned up'); fetchActiveLinks(); }
    catch { toast.error('Cleanup failed'); }
    finally { setLoading(false); }
  };

  const handleCopyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/clientaccesspage/${token}`);
    toast.success('Copied to clipboard!');
  };

  const toggleCandidate = (id) => setSelectedCandidates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filteredCandidates = statusFilter === 'all'
    ? candidates
    : candidates.filter(c => {
        const s = c.status || '';
        if (statusFilter === 'Interview_Scheduled') return s === 'Interview_Scheduled' || s === 'Interview Scheduled';
        if (statusFilter === 'Slotassigned') return s === 'Slotassigned';
        return s === statusFilter;
      });

  const toggleAll = () => setSelectedCandidates(
    selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0
      ? []
      : filteredCandidates.map(c => c.id)
  );

  const canShare = selectedClient && selectedJob && selectedCandidates.length > 0;
  const filteredClients = clients.filter(c =>
    [c.companyName, c.pocName, c.pocEmail, c.id].some(v => v?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredLinks = activeLinks.filter(link => {
    if (linkFilter === 'active')  return !link.isExpired && !link.isRevoked;
    if (linkFilter === 'expired') return link.isExpired;
    if (linkFilter === 'revoked') return link.isRevoked;
    return true;
  });

  const counts = {
    all: activeLinks.length,
    active: activeLinks.filter(l => !l.isExpired && !l.isRevoked).length,
    expired: activeLinks.filter(l => l.isExpired).length,
    revoked: activeLinks.filter(l => l.isRevoked).length,
  };

  const statusCounts = STATUS_FILTERS.reduce((acc, f) => {
    if (f.key === 'all') { acc.all = candidates.length; return acc; }
    acc[f.key] = candidates.filter(c => {
      const s = c.status || '';
      if (f.key === 'Interview_Scheduled') return s === 'Interview_Scheduled' || s === 'Interview Scheduled';
      if (f.key === 'Slotassigned') return s === 'Slotassigned';
      return s === f.key;
    }).length;
    return acc;
  }, {});

  const showGivenCTC       = statusFilter === 'Selected';
  const showInterviewSlots = statusFilter === 'Interview_Scheduled' || statusFilter === 'Slotassigned';
  const showRejectionBtn   = statusFilter === 'Rejected';
  const showScheduleBtn    = statusFilter === 'Shortlisted';

  return (
    <div className="flex h-screen bg-slate-50 font-sans">

      {/* ── Modals ── */}
      {showShareModal && (
        <ShareCandidatesModal
          initialClient={selectedClient}
          initialJob={selectedJob}
          selectedCandidateIds={selectedCandidates}
          onClose={() => {
            setShowShareModal(false);
            setSelectedCandidates([]);
          }}
        />
      )}
      {scheduleCandidate && (
        <ScheduleInterviewModal
          candidate={scheduleCandidate}
          token={scheduleToken}
          onClose={() => { setScheduleCandidate(null); setScheduleToken(null); }}
          onScheduled={() => { setScheduleCandidate(null); setScheduleToken(null); fetchCandidatesByJobId(selectedJob.id); }}
        />
      )}
      {confirmSlotCandidate && (
        <ConfirmSlotModal
          candidate={confirmSlotCandidate}
          selectedJob={selectedJob}
          onClose={() => setConfirmSlotCandidate(null)}
          onScheduled={() => { setConfirmSlotCandidate(null); fetchCandidatesByJobId(selectedJob.id); }}
        />
      )}
      {rejectionCandidate && (
        <RejectionReasonModal candidate={rejectionCandidate} onClose={() => setRejectionCandidate(null)} />
      )}

      {/* ── Sidebar ── */}
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Users size={17} className="text-indigo-600" /> Clients
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">{clients.length} total</p>
        </div>
        <div className="p-3 border-b border-slate-200">
          <input type="text" placeholder="Search clients…" value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredClients.map(client => (
            <div key={client.id} onClick={() => setSelectedClient(client)}
              className={`p-3 border-b border-slate-100 cursor-pointer transition-colors ${selectedClient?.id === client.id ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : 'hover:bg-slate-50'}`}>
              <div className="flex items-start gap-2">
                <Building2 size={15} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{client.companyName || 'Unnamed'}</div>
                  <div className="text-xs font-medium text-slate-600 truncate mt-0.5">{client.pocName}</div>
                  <div className="text-xs text-slate-400 truncate">{client.pocEmail}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Top header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-slate-900">
                {selectedClient ? selectedClient.companyName : 'Select a Client'}
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                {selectedClient
                  ? selectedJob
                    ? `${filteredCandidates.length} candidates${statusFilter !== 'all' ? ` (${statusFilter.replace(/_/g,' ')})` : ''} · ${selectedCandidates.length} selected`
                    : `${jobs.length} jobs — select one to view candidates`
                  : 'Choose a client from the left'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchActiveLinks} disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors disabled:opacity-50">
                <Link size={13} /> Active Links
              </button>
              <button
                onClick={() => {
                  if (!canShare) {
                    toast.error(!selectedClient ? 'Select a client first' : !selectedJob ? 'Select a job first' : 'Select at least one candidate');
                    return;
                  }
                  setShowShareModal(true);
                }}
                disabled={!canShare || loading}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm shadow-indigo-200">
                <Share2 size={13} /> Share to Client
                {selectedCandidates.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-white text-xs">{selectedCandidates.length}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Job selector */}
        {selectedClient && (
          <div className="bg-white border-b border-slate-200 px-6 py-3 flex-shrink-0">
            <div className="flex items-center gap-3">
              <Briefcase size={15} className="text-slate-400 flex-shrink-0" />
              <select
                value={selectedJob?.id || ''}
                onChange={e => { const j = jobs.find(j => j.id === e.target.value); setSelectedJob(j || null); setSelectedCandidates([]); }}
                disabled={jobs.length === 0}
                className="flex-1 text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
                <option value="">{jobs.length === 0 ? '— No jobs for this client —' : '— Select a job —'}</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.jobCode} · {j.jobTitle}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Status Filter Tabs — all use blue as active color */}
        {selectedJob && candidates.length > 0 && (
          <div className="bg-white border-b border-slate-200 px-6 py-2.5 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={13} className="text-slate-400 flex-shrink-0" />
              {STATUS_FILTERS.map(f => {
                const isActive = statusFilter === f.key;
                return (
                  <button key={f.key}
                    onClick={() => { setStatusFilter(f.key); setSelectedCandidates([]); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${isActive ? ACTIVE_TAB : INACTIVE_TAB}`}>
                    {f.label}
                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {statusCounts[f.key] ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Candidates table */}
        <div className="flex-1 overflow-auto p-6">
          {!selectedClient ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Users size={48} className="mb-3 opacity-30" /><p className="text-sm font-medium">No client selected</p>
            </div>
          ) : !selectedJob ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Briefcase size={48} className="mb-3 opacity-30" />
              <p className="text-sm font-medium">{jobs.length === 0 ? 'No jobs for this client' : 'Select a job above'}</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader size={28} className="animate-spin text-indigo-500" />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-400">
              <AlertCircle size={36} className="mb-2 opacity-30" />
              <p className="text-sm">No {statusFilter === 'all' ? '' : statusFilter.replace(/_/g, ' ')} candidates</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                <table className="min-w-full text-sm divide-y divide-slate-100">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 sticky left-0 bg-slate-50 w-10">
                        <input type="checkbox"
                          checked={selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0}
                          onChange={toggleAll}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" />
                      </th>
                      {['Name','Email','Phone','Current Role','Experience','Current CTC','Expected CTC','Notice','Status'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                      {showGivenCTC && (
                        <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-600 uppercase tracking-wider whitespace-nowrap">
                          <span className="flex items-center gap-1"><DollarSign size={12} /> Given CTC</span>
                        </th>
                      )}
                      {showInterviewSlots && (
                        <th className="px-4 py-3 text-left text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap">
                          <span className="flex items-center gap-1"><Calendar size={12} /> Interview Slots</span>
                        </th>
                      )}
                      {showRejectionBtn && (
                        <th className="px-4 py-3 text-left text-xs font-semibold text-red-500 uppercase tracking-wider whitespace-nowrap">
                          <span className="flex items-center gap-1"><MessageSquare size={12} /> Reason</span>
                        </th>
                      )}
                      {showScheduleBtn && (
                        <th className="px-4 py-3 text-left text-xs font-semibold text-indigo-600 uppercase tracking-wider whitespace-nowrap">
                          <span className="flex items-center gap-1"><Calendar size={12} /> Schedule</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredCandidates.map(c => (
                      <tr key={c.id} className={`transition-colors hover:bg-slate-50 ${selectedCandidates.includes(c.id) ? 'bg-indigo-50/60' : ''}`}>
                        <td className="px-4 py-3 sticky left-0 bg-inherit">
                          <input type="checkbox" checked={selectedCandidates.includes(c.id)} onChange={() => toggleCandidate(c.id)}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-semibold text-slate-900">{c.firstName} {c.lastName}</div>
                          <div className="text-xs text-slate-400">{c.applicationId}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{c.primaryEmail}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{c.primaryPhone || 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{c.currentJobTitle || 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{c.totalYearsOfExperience || '0'} yrs</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                          {c.currentCTC ? `₹${Number(c.currentCTC).toLocaleString('en-IN')}` : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                          {c.expectedCTC ? `₹${Number(c.expectedCTC).toLocaleString('en-IN')}` : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{c.noticePeriod || 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(c.status)}`}>
                            {c.status?.replace(/_/g, ' ') || '—'}
                          </span>
                        </td>

                        {showGivenCTC && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            {c.givenCTC ? (
                              <span className="font-semibold text-emerald-700">₹{Number(c.givenCTC).toLocaleString('en-IN')}</span>
                            ) : (
                              <span className="text-slate-400 text-xs italic">Not set</span>
                            )}
                          </td>
                        )}

                        {showInterviewSlots && (
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <div className="flex flex-col gap-1 max-h-32 overflow-y-auto pr-0.5" style={{ minWidth: '180px' }}>
                                {Array.isArray(c.timeslots) && c.timeslots.length > 0 ? (
                                  c.timeslots.map((slot, i) => {
                                    const isCandidatePick = c.selectedSlot && c.selectedSlot === slot;
                                    return (
                                      <span key={i} className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap flex items-center gap-1 ${
                                        isCandidatePick
                                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold'
                                          : 'bg-blue-50 text-blue-700 border-blue-100'
                                      }`}>
                                        {isCandidatePick && <CheckCircle size={10} className="flex-shrink-0" />}
                                        {new Date(slot).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                                      </span>
                                    );
                                  })
                                ) : (
                                  <span className="text-xs text-slate-400 italic">No slots</span>
                                )}
                              </div>
                              <button onClick={() => setConfirmSlotCandidate(c)}
                                className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors whitespace-nowrap flex-shrink-0 mt-0.5">
                                <Calendar size={11} /> Schedule
                              </button>
                            </div>
                            {c.selectedSlot && (
                              <div className="mt-1.5 flex items-center gap-1 text-xs text-emerald-700 font-medium">
                                <CheckCircle size={11} />
                                Candidate picked · {new Date(c.selectedSlot).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                              </div>
                            )}
                          </td>
                        )}

                        {showRejectionBtn && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button onClick={() => setRejectionCandidate(c)}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition-colors">
                              <MessageSquare size={11} />
                              {c.reason || c.rejectionReason ? 'View Reason' : 'No Reason'}
                            </button>
                          </td>
                        )}
                        {showScheduleBtn && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => {
                                const matchingLink = activeLinks.find(
                                  link => link.jobId === selectedJob.jobCode &&
                                  link.applicationIds.includes(c.id)
                                );
                                setScheduleCandidate(c);
                                setScheduleToken(matchingLink?.token || null);
                              }}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg transition-colors">
                              <Calendar size={11} /> Schedule Interview
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Active Links Panel ── */}
      {showLinksPanel && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowLinksPanel(false)} />
          <div className="fixed right-0 top-0 z-50 bg-white shadow-2xl flex flex-col"
            style={{ width: '420px', height: '100vh', animation: 'slideIn .25s ease-out' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                <Link size={14} className="text-indigo-600" /> Shareable Links
              </h2>
              <div className="flex items-center gap-1.5">
                <button onClick={handleCleanup} className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100">
                  <Trash2 size={11} /> Clean
                </button>
                <button onClick={() => setShowLinksPanel(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-1 px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex-shrink-0">
              {[{ key: 'all', label: 'All' }, { key: 'active', label: 'Active' }, { key: 'expired', label: 'Expired' }, { key: 'revoked', label: 'Revoked' }].map(tab => (
                <button key={tab.key} onClick={() => setLinkFilter(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    linkFilter === tab.key
                      ? tab.key === 'active' ? 'bg-emerald-600 text-white'
                        : tab.key === 'expired' ? 'bg-red-500 text-white'
                        : tab.key === 'revoked' ? 'bg-slate-500 text-white'
                        : 'bg-indigo-600 text-white'
                      : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'
                  }`}>
                  {tab.label}
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${linkFilter === tab.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {counts[tab.key]}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2.5">
              {filteredLinks.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <Link size={32} className="mb-2 opacity-30" />
                  <p className="text-sm">No {linkFilter === 'all' ? '' : linkFilter} links</p>
                </div>
              ) : filteredLinks.map(link => (
                <div key={link.id} className="border border-slate-200 rounded-xl p-3 bg-white hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Mail size={12} className="text-slate-400 flex-shrink-0" />
                      <span className="text-xs font-semibold text-slate-800 truncate">{link.clientEmail}</span>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${
                      link.isExpired ? 'bg-red-100 text-red-700' : link.isRevoked ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {link.isExpired ? 'Expired' : link.isRevoked ? 'Revoked' : 'Active'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 space-y-0.5 mb-2">
                    <p><span className="text-slate-400">Job:</span> <b>{link.jobId}</b></p>
                    <p>
                      <span className="text-slate-400">Candidates:</span> <b>{link.applicationIds?.length || 0}</b>
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="text-slate-400">Views:</span> <b>{link.accessCount || 0}</b>
                    </p>
                    {link.expiresAt && <p><span className="text-slate-400">Expires:</span> {formatDate(link.expiresAt)}</p>}
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-1 text-xs font-mono text-slate-500 mb-2 break-all border border-slate-100">
                    {window.location.origin}/clientaccesspage/{link.token}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => handleCopyLink(link.token)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                      <Copy size={11} /> Copy
                    </button>
                    {!link.isRevoked && !link.isExpired && (
                      <>
                        <button onClick={() => handleExtendLink(link.id)}
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg">
                          <Clock size={11} /> Extend
                        </button>
                        <button onClick={() => handleRevokeLink(link.id)}
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium bg-red-50 hover:bg-red-100 text-red-600 rounded-lg">
                          <XCircle size={11} /> Revoke
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 px-4 py-2.5 bg-slate-50 flex-shrink-0">
              <p className="text-xs text-slate-500 text-center">
                Showing <b>{filteredLinks.length}</b> of <b>{activeLinks.length}</b> · Active <b>{counts.active}</b> · Expired <b>{counts.expired}</b> · Revoked <b>{counts.revoked}</b>
              </p>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
};

export default ClientCandidateSharing;